import { Typography } from '@mui/material';
import {
  EVENTS_FILTER_KEYS,
  FilterKeys,
  FilterSection,
  GetEventsQueryFilters,
  RepetitionType,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  AgeGroupExpandedSelect,
  DateRangeDropdown,
  EventRecurrenceExpandedSelect,
  EventRecurrenceTagSelect,
  EventsParticipationOptions,
  EventsStatusOptions,
  ExpandedSelectFilter,
  FilterDropdown,
  FiltersContainer,
  FiltersSidebarMethods,
  getEventsStatusLabel,
  getParticipantLabel,
  GroupExpandedSelect,
  GroupTagSelect,
  PersonalFiltersDropdown,
  pickOnlyParamsFromFilterKeys,
  PropertyTypeExpandedSelect,
  PropertyTypeTagSelect,
  renderPropertyGroupTags,
} from '@schooly/components/filters';
import { SchoolPropertyType, SchoolUserRole } from '@schooly/constants';
import { TagSelect } from '@schooly/style';
import { FC, useCallback, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';

import { useSchool } from '../../hooks/useSchool';
import useSchoolYears from '../../hooks/useSchoolYears';
import { EventsFiltersSidebar } from './EventsFiltersSidebar';
import { useEventsFilters } from './useEventsFilters';

type EventsFiltersProps = {
  schoolId: string;
  filters: GetEventsQueryFilters;
  defaultFilters: GetEventsQueryFilters;
  defaultUserFilters: GetEventsQueryFilters;
  onSetFilters: (v: GetEventsQueryFilters) => void;
  notActualInitialDate?: boolean;
};

export const EventsFilters: FC<EventsFiltersProps> = ({
  defaultFilters,
  defaultUserFilters,
  onSetFilters,
  filters,
  schoolId,
  notActualInitialDate,
}) => {
  const sidebar = useRef<FiltersSidebarMethods | null>(null);
  const personalFiltersDropdown = useRef<PersonalFiltersDropdown | null>(null);
  const { currentStaff, permissions } = useAuth();
  const { formatMessage } = useIntl();
  const { defaultValidity } = useSchoolYears();
  const { hasHouses } = useSchool();

  const canViewGroups = permissions.includes('group_viewer');

  const {
    labels,
    filtersDate,
    filtersEventStatus,
    filtersInviteType,
    filtersGroup,
    filtersHouse,
    filtersRecurrenceId,
    filtersAgeGroup,
    selectedItemsForAgeGroups,
    getAgeGroupById,
    getAgeGroupsByLevelId,
    getSchoolLevelById,
    onSetAgeGroup,
    onClearAgeGroup,
    onSetDate,
    onSetEventStatus,
    onClearEventStatus,
    onSetInviteType,
    onClearInviteType,
    onSetGroup,
    onClearGroup,
    onSetHouse,
    onClearHouse,
    onSetRecurrenceId,
    onClearRecurrenceId,
  } = useEventsFilters({ filters, onSetFilters, schoolId });

  const handleSaveFilter = useCallback(() => {
    personalFiltersDropdown.current?.saveFilter();
  }, []);

  const intersectDateFilter = useMemo(
    () => ({ [FilterKeys.IntersectDate]: filters.date }),
    [filters.date],
  );

  const recurringFilter = useMemo(
    () => ({
      [FilterKeys.Date]: filters.date,
      [FilterKeys.RepetitionType]: [RepetitionType.FirstOfRecurrence],
    }),
    [filters.date],
  );

  return (
    <FiltersContainer onOpenFilters={() => sidebar.current?.open()}>
      <PersonalFiltersDropdown
        defaultSchoolYear={defaultValidity}
        ref={personalFiltersDropdown}
        currentUser={currentStaff}
        relationId={currentStaff?.relation_id || ''}
        onSaveFilter={handleSaveFilter}
        schoolId={schoolId}
        section={FilterSection.Events}
        filters={filters}
        onSetFilters={(v) => {
          onSetFilters(pickOnlyParamsFromFilterKeys(EVENTS_FILTER_KEYS, v));
        }}
      />
      {filtersDate && (
        <DateRangeDropdown
          schoolId={schoolId}
          date={filtersDate}
          onSetDate={onSetDate}
          openLabel={labels[FilterKeys.Date]}
          defaultSchoolYear={defaultValidity}
          notActualInitialDate={notActualInitialDate}
          schoolYearFromToday
        />
      )}

      {!!filtersEventStatus?.length && (
        <FilterDropdown
          onClear={onClearEventStatus}
          label={labels[FilterKeys.EventStatus]}
          tags={(open) =>
            filtersEventStatus.map((status) => (
              <TagSelect
                key={status}
                sx={{ maxWidth: 200 }}
                onClick={open}
                label={formatMessage({ id: getEventsStatusLabel(status) })}
              />
            ))
          }
        >
          {(onClose) => (
            <ExpandedSelectFilter
              selected={filtersEventStatus}
              onApply={onSetEventStatus}
              onClose={onClose}
              onClear={onClearEventStatus}
              options={EventsStatusOptions}
              getLabel={(s) => formatMessage({ id: getEventsStatusLabel(s) })}
            />
          )}
        </FilterDropdown>
      )}

      {!!filtersInviteType?.length && (
        <FilterDropdown
          onClear={onClearInviteType}
          label={labels[FilterKeys.InviteType]}
          tags={(open) =>
            filtersInviteType.map((inviteType) => (
              <TagSelect
                key={inviteType}
                sx={{ maxWidth: 200 }}
                onClick={open}
                label={formatMessage({ id: getParticipantLabel(inviteType) })}
              />
            ))
          }
        >
          {(onClose) => (
            <ExpandedSelectFilter
              selected={filtersInviteType}
              onApply={onSetInviteType}
              onClose={onClose}
              onClear={onClearInviteType}
              options={EventsParticipationOptions}
              getLabel={(i) => formatMessage({ id: getParticipantLabel(i) })}
            />
          )}
        </FilterDropdown>
      )}

      {!!filtersAgeGroup?.length && (
        <FilterDropdown
          onClear={onClearAgeGroup}
          label={labels[FilterKeys.AgeGroup]}
          tags={(open) =>
            renderPropertyGroupTags({
              selectedItems: selectedItemsForAgeGroups,
              onClick: open,
              getProperty: (i) =>
                i.isGroup
                  ? getSchoolLevelById(i.id)
                  : { ...getAgeGroupById(i.id), type: SchoolPropertyType.AgeGroup },
              getTooltip: (i) =>
                i.isGroup
                  ? getAgeGroupsByLevelId(i.id).map((ageGroup) => (
                      <Typography key={ageGroup.id}>{ageGroup.name}</Typography>
                    ))
                  : null,
              tagProps: {
                userRole: SchoolUserRole.Student,
                sx: { maxWidth: 200 },
              },
            })
          }
        >
          {(onClose) => (
            <AgeGroupExpandedSelect
              userRole={SchoolUserRole.Student}
              schoolId={schoolId}
              selectedIds={filtersAgeGroup}
              onApply={onSetAgeGroup}
              onClose={onClose}
              onClear={onClearAgeGroup}
            />
          )}
        </FilterDropdown>
      )}

      {!!filtersHouse?.length && hasHouses && (
        <FilterDropdown
          onClear={onClearHouse}
          label={labels[FilterKeys.House]}
          tags={(open) =>
            filtersHouse.map((id) => (
              <PropertyTypeTagSelect
                userRole={SchoolUserRole.Student}
                sx={{ maxWidth: 200 }}
                schoolId={schoolId}
                key={id}
                id={id}
                onClick={open}
              />
            ))
          }
        >
          {(onClose) => (
            <PropertyTypeExpandedSelect
              propertyType={SchoolPropertyType.House}
              userRole={SchoolUserRole.Student}
              schoolId={schoolId}
              selectedIds={filtersHouse}
              onApply={onSetHouse}
              onClose={onClose}
              onClear={onClearHouse}
            />
          )}
        </FilterDropdown>
      )}

      {!!filtersGroup?.length && canViewGroups && (
        <FilterDropdown
          onClear={onClearGroup}
          label={labels[FilterKeys.Group]}
          width={500}
          tags={(open) =>
            filtersGroup.map((id) => (
              <GroupTagSelect key={id} sx={{ maxWidth: 200 }} id={id} onClick={open} />
            ))
          }
        >
          {(onClose) => (
            <GroupExpandedSelect
              selectedIds={filtersGroup}
              schoolId={schoolId}
              onApply={onSetGroup}
              onClose={onClose}
              onClear={onClearGroup}
              filters={intersectDateFilter}
            />
          )}
        </FilterDropdown>
      )}

      {!!filtersRecurrenceId?.length && (
        <FilterDropdown
          width={500}
          onClear={onClearRecurrenceId}
          label={labels[FilterKeys.RecurrenceId]}
          tags={(open) =>
            filtersRecurrenceId.map((id) => (
              <EventRecurrenceTagSelect
                key={id}
                sx={{ maxWidth: 200 }}
                id={id}
                onClick={open}
                schoolId={schoolId}
                onClear={() => onClearRecurrenceId()}
              />
            ))
          }
        >
          {(onClose) => (
            <EventRecurrenceExpandedSelect
              selectedId={filtersRecurrenceId[0]}
              schoolId={schoolId}
              onSelectRecurrence={(recurrenceId) => {
                onSetRecurrenceId([recurrenceId]);
              }}
              onClose={onClose}
              onClear={onClearRecurrenceId}
              filters={recurringFilter}
            />
          )}
        </FilterDropdown>
      )}

      <EventsFiltersSidebar
        ref={sidebar}
        defaultFilters={defaultFilters}
        filters={filters}
        onApply={onSetFilters}
        schoolId={schoolId}
        defaultSchoolYear={defaultValidity}
      />
    </FiltersContainer>
  );
};
